import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import CommonFormLayout from "../../OptmisedCode/Form/CommonFormLayout";
import { MAIN_URL } from "../../../Configurations/Urls";

const BASE_ROUTE = "/organization/client/organizations";
const ORGANIZATION_TYPES = ["Client", "Testing", "Demo", "Internal"];

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function editionKey(edition) {
  return String(edition?.application_edition_id ?? "");
}

function mergeCatalogEditions(linkedRows, catalogApplications) {
  const catalogByApplication = new Map(
    (catalogApplications || []).map((row) => [
      Number(row.application_id),
      row.editions || [],
    ])
  );

  return (linkedRows || []).map((row) => {
    const catalogEditions = catalogByApplication.get(Number(row.application_id));
    const merged = [
      ...(catalogEditions && catalogEditions.length > 0
        ? catalogEditions
        : row.editions || []),
    ];
    const currentId = Number(row.application_edition_id);
    const hasCurrent = merged.some(
      (edition) => Number(edition.application_edition_id) === currentId
    );

    if (currentId > 0 && !hasCurrent) {
      merged.unshift({
        application_edition_id: currentId,
        edition_name: row.edition_name || "Current edition",
        code: row.edition_code || "",
        is_default: false,
      });
    }

    const seen = new Set();
    const editions = merged.filter((edition) => {
      const key = editionKey(edition);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    return { ...row, editions };
  });
}

export default function ClientOrganizationEdit({ mode = "edit" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isView = mode === "view";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [organizationType, setOrganizationType] = useState("Client");
  const [applicationEditions, setApplicationEditions] = useState([]);

  const loadOrganization = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const headers = authHeaders();
      const [orgRes, catalogRes] = await Promise.all([
        axios.get(`${MAIN_URL}/api/organizations/${id}`, { headers }),
        axios
          .get(`${MAIN_URL}/api/client-management/organizations/create-options`, {
            headers,
          })
          .catch(() => ({ data: { applications: [] } })),
      ]);
      const organization = orgRes.data?.organization || {};
      setOrganizationName(organization.organization_name || "");
      setOrganizationType(organization.organization_type || "Client");
      setApplicationEditions(
        mergeCatalogEditions(
          organization.application_editions || [],
          catalogRes.data?.applications || []
        )
      );
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load organization."
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  const handleEditionChange = (applicationId, editionId) => {
    setApplicationEditions((current) =>
      current.map((row) =>
        Number(row.application_id) === Number(applicationId)
          ? { ...row, application_edition_id: Number(editionId) }
          : row
      )
    );
  };

  const handleSave = async () => {
    if (!organizationName.trim()) {
      toast.error("Organization name is required.");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${MAIN_URL}/api/organizations/${id}`,
        {
          organization_name: organizationName.trim(),
          organization_type: organizationType,
          application_editions: applicationEditions
            .filter(
              (row) => Number(row.application_id) > 0 && Number(row.application_edition_id) > 0
            )
            .map((row) => ({
              application_id: Number(row.application_id),
              application_edition_id: Number(row.application_edition_id),
            })),
        },
        { headers: authHeaders() }
      );
      toast.success("Organization updated.");
      navigate(
        { pathname: BASE_ROUTE, search: location.search },
        {
          replace: true,
          state: { refreshOrganizations: Date.now() },
        }
      );
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Save failed."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonFormLayout
      mode={mode}
      loading={loading}
      btnLoading={saving}
      onSubmit={handleSave}
      wrapInGrid={false}
      headerProps={{
        addMessage: "Client organization",
        updateMessage: "Edit client organization",
        homeLink: BASE_ROUTE,
        homeText: "Client organizations",
        editLink: id ? `${BASE_ROUTE}/edit/${id}` : undefined,
      }}
    >
      <div className="ems-fm-grid">
        <div className="ems-fm-field">
          <label>Organization name</label>
          <input
            className="ems-fm-input"
            value={organizationName}
            readOnly={isView}
            onChange={(event) => setOrganizationName(event.target.value)}
          />
        </div>
        <div className="ems-fm-field">
          <label>Organization type</label>
          <select
            className="ems-fm-input"
            value={organizationType}
            disabled={isView}
            onChange={(event) => setOrganizationType(event.target.value)}
          >
            {ORGANIZATION_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ems-fm-section" style={{ marginTop: 8 }}>
        <div className="ems-fm-section-head">
          <div className="ems-fm-section-title">Application editions</div>
          <div className="ems-fm-section-sub">
            Choose the edition for each linked application. This is independent of organization type.
          </div>
        </div>

        {applicationEditions.length === 0 ? (
          <p className="ems-fd-sub">
            This organization has no linked applications with editions.
          </p>
        ) : (
          <div className="ems-fm-grid">
            {applicationEditions.map((row) => (
              <div className="ems-fm-field full" key={row.application_id}>
                <label>{row.application_name} edition</label>
                <select
                  className="ems-fm-input"
                  value={
                    row.application_edition_id
                      ? String(row.application_edition_id)
                      : ""
                  }
                  disabled={isView}
                  onChange={(event) =>
                    handleEditionChange(row.application_id, event.target.value)
                  }
                >
                  {(row.editions || []).length === 0 ? (
                    <option value="">No editions available</option>
                  ) : null}
                  {(row.editions || []).map((edition) => (
                    <option
                      key={edition.application_edition_id}
                      value={String(edition.application_edition_id)}
                    >
                      {edition.edition_name}
                      {edition.code ? ` (${edition.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </CommonFormLayout>
  );
}
